import React, { useState } from 'react';
import './Settings.css';

interface SystemSettings {
  siteName: string;
  siteUrl: string;
  adminEmail: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  defaultLanguage: string;
  maintenanceMode: boolean;
  enableRegistration: boolean;
  requireEmailVerification: boolean;
}

interface TrackingSettings {
  trackingDomain: string;
  defaultRedirectUrl: string;
  cookieLifetime: number;
  enableBotDetection: boolean;
  enableFraudDetection: boolean;
  maxClicksPerIP: number;
  attributionWindow: number;
  enableGeoTracking: boolean;
}

interface PaymentSettings {
  minimumPayout: number;
  paymentFrequency: 'weekly' | 'biweekly' | 'monthly';
  paymentMethods: string[];
  transactionFeePercentage: number;
  currencyExchangeProvider: string;
  autoPayoutThreshold: number;
  enableAutoPayouts: boolean;
}

interface SecuritySettings {
  sessionTimeout: number;
  maxLoginAttempts: number;
  enableTwoFA: boolean;
  allowedIpRanges: string[];
  enableAuditLog: boolean;
  passwordMinLength: number;
  passwordRequireSpecialChars: boolean;
  enableApiRateLimit: boolean;
  apiRateLimitPerMinute: number;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'system' | 'tracking' | 'payment' | 'security'>('system');
  const [hasChanges, setHasChanges] = useState(false);

  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    siteName: 'AdBizz Dashboard',
    siteUrl: 'https://dashboard.adbizz.com',
    adminEmail: 'admin@adbizz.com',
    timezone: 'America/New_York',
    dateFormat: 'YYYY-MM-DD',
    currency: 'USD',
    defaultLanguage: 'en',
    maintenanceMode: false,
    enableRegistration: true,
    requireEmailVerification: true
  });

  const [trackingSettings, setTrackingSettings] = useState<TrackingSettings>({
    trackingDomain: 'track.adbizz.com',
    defaultRedirectUrl: 'https://adbizz.com/default',
    cookieLifetime: 30,
    enableBotDetection: true,
    enableFraudDetection: true,
    maxClicksPerIP: 100,
    attributionWindow: 24,
    enableGeoTracking: true
  });

  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({
    minimumPayout: 100,
    paymentFrequency: 'monthly',
    paymentMethods: ['PayPal', 'Bank Transfer', 'Wise'],
    transactionFeePercentage: 2.5,
    currencyExchangeProvider: 'xe.com',
    autoPayoutThreshold: 1000,
    enableAutoPayouts: false
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    sessionTimeout: 1440, // 24 hours in minutes
    maxLoginAttempts: 5,
    enableTwoFA: false,
    allowedIpRanges: ['0.0.0.0/0'],
    enableAuditLog: true,
    passwordMinLength: 8,
    passwordRequireSpecialChars: true,
    enableApiRateLimit: true,
    apiRateLimitPerMinute: 1000
  });

  const handleSystemChange = (field: keyof SystemSettings, value: any) => {
    setSystemSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTrackingChange = (field: keyof TrackingSettings, value: any) => {
    setTrackingSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handlePaymentChange = (field: keyof PaymentSettings, value: any) => {
    setPaymentSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSecurityChange = (field: keyof SecuritySettings, value: any) => {
    setSecuritySettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Here you would save to backend
    console.log('Saving settings:', {
      system: systemSettings,
      tracking: trackingSettings,
      payment: paymentSettings,
      security: securitySettings
    });

    alert('Settings saved successfully!');
    setHasChanges(false);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      // Reset to default values
      window.location.reload();
    }
  };

  const renderSystemSettings = () => (
    <div className="settings-section">
      <h2>System Configuration</h2>

      <div className="settings-grid">
        <div className="setting-group">
          <h3>General</h3>
          <div className="setting-item">
            <label>Site Name</label>
            <input
              type="text"
              value={systemSettings.siteName}
              onChange={(e) => handleSystemChange('siteName', e.target.value)}
              className="setting-input"
            />
          </div>
          <div className="setting-item">
            <label>Site URL</label>
            <input
              type="url"
              value={systemSettings.siteUrl}
              onChange={(e) => handleSystemChange('siteUrl', e.target.value)}
              className="setting-input"
            />
          </div>
          <div className="setting-item">
            <label>Admin Email</label>
            <input
              type="email"
              value={systemSettings.adminEmail}
              onChange={(e) => handleSystemChange('adminEmail', e.target.value)}
              className="setting-input"
            />
          </div>
        </div>

        <div className="setting-group">
          <h3>Localization</h3>
          <div className="setting-item">
            <label>Timezone</label>
            <select
              value={systemSettings.timezone}
              onChange={(e) => handleSystemChange('timezone', e.target.value)}
              className="setting-select"
            >
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Europe/Berlin">Europe/Berlin (CET)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Date Format</label>
            <select
              value={systemSettings.dateFormat}
              onChange={(e) => handleSystemChange('dateFormat', e.target.value)}
              className="setting-select"
            >
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="DD-MM-YYYY">DD-MM-YYYY</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Currency</label>
            <select
              value={systemSettings.currency}
              onChange={(e) => handleSystemChange('currency', e.target.value)}
              className="setting-select"
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
            </select>
          </div>
        </div>

        <div className="setting-group">
          <h3>User Management</h3>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={systemSettings.enableRegistration}
                onChange={(e) => handleSystemChange('enableRegistration', e.target.checked)}
              />
              Enable User Registration
            </label>
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={systemSettings.requireEmailVerification}
                onChange={(e) => handleSystemChange('requireEmailVerification', e.target.checked)}
              />
              Require Email Verification
            </label>
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={systemSettings.maintenanceMode}
                onChange={(e) => handleSystemChange('maintenanceMode', e.target.checked)}
              />
              Maintenance Mode
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTrackingSettings = () => (
    <div className="settings-section">
      <h2>Tracking Configuration</h2>

      <div className="settings-grid">
        <div className="setting-group">
          <h3>Domain & URLs</h3>
          <div className="setting-item">
            <label>Tracking Domain</label>
            <input
              type="text"
              value={trackingSettings.trackingDomain}
              onChange={(e) => handleTrackingChange('trackingDomain', e.target.value)}
              className="setting-input"
            />
          </div>
          <div className="setting-item">
            <label>Default Redirect URL</label>
            <input
              type="url"
              value={trackingSettings.defaultRedirectUrl}
              onChange={(e) => handleTrackingChange('defaultRedirectUrl', e.target.value)}
              className="setting-input"
            />
          </div>
        </div>

        <div className="setting-group">
          <h3>Attribution & Cookies</h3>
          <div className="setting-item">
            <label>Cookie Lifetime (days)</label>
            <input
              type="number"
              value={trackingSettings.cookieLifetime}
              onChange={(e) => handleTrackingChange('cookieLifetime', parseInt(e.target.value))}
              className="setting-input"
              min="1"
              max="365"
            />
          </div>
          <div className="setting-item">
            <label>Attribution Window (hours)</label>
            <input
              type="number"
              value={trackingSettings.attributionWindow}
              onChange={(e) => handleTrackingChange('attributionWindow', parseInt(e.target.value))}
              className="setting-input"
              min="1"
              max="720"
            />
          </div>
        </div>

        <div className="setting-group">
          <h3>Security & Fraud</h3>
          <div className="setting-item">
            <label>Max Clicks per IP (per hour)</label>
            <input
              type="number"
              value={trackingSettings.maxClicksPerIP}
              onChange={(e) => handleTrackingChange('maxClicksPerIP', parseInt(e.target.value))}
              className="setting-input"
              min="1"
              max="1000"
            />
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={trackingSettings.enableBotDetection}
                onChange={(e) => handleTrackingChange('enableBotDetection', e.target.checked)}
              />
              Enable Bot Detection
            </label>
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={trackingSettings.enableFraudDetection}
                onChange={(e) => handleTrackingChange('enableFraudDetection', e.target.checked)}
              />
              Enable Fraud Detection
            </label>
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={trackingSettings.enableGeoTracking}
                onChange={(e) => handleTrackingChange('enableGeoTracking', e.target.checked)}
              />
              Enable Geo Tracking
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="settings-section">
      <h2>Payment Configuration</h2>

      <div className="settings-grid">
        <div className="setting-group">
          <h3>Payout Settings</h3>
          <div className="setting-item">
            <label>Minimum Payout Amount</label>
            <input
              type="number"
              value={paymentSettings.minimumPayout}
              onChange={(e) => handlePaymentChange('minimumPayout', parseFloat(e.target.value))}
              className="setting-input"
              min="0"
              step="0.01"
            />
          </div>
          <div className="setting-item">
            <label>Payment Frequency</label>
            <select
              value={paymentSettings.paymentFrequency}
              onChange={(e) => handlePaymentChange('paymentFrequency', e.target.value)}
              className="setting-select"
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="setting-item">
            <label>Transaction Fee (%)</label>
            <input
              type="number"
              value={paymentSettings.transactionFeePercentage}
              onChange={(e) => handlePaymentChange('transactionFeePercentage', parseFloat(e.target.value))}
              className="setting-input"
              min="0"
              max="10"
              step="0.1"
            />
          </div>
        </div>

        <div className="setting-group">
          <h3>Auto Payouts</h3>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={paymentSettings.enableAutoPayouts}
                onChange={(e) => handlePaymentChange('enableAutoPayouts', e.target.checked)}
              />
              Enable Automatic Payouts
            </label>
          </div>
          <div className="setting-item">
            <label>Auto Payout Threshold</label>
            <input
              type="number"
              value={paymentSettings.autoPayoutThreshold}
              onChange={(e) => handlePaymentChange('autoPayoutThreshold', parseFloat(e.target.value))}
              className="setting-input"
              min="0"
              step="0.01"
              disabled={!paymentSettings.enableAutoPayouts}
            />
          </div>
        </div>

        <div className="setting-group">
          <h3>Payment Methods</h3>
          <div className="setting-item">
            <label>Available Methods</label>
            <div className="payment-methods">
              {['PayPal', 'Bank Transfer', 'Wise', 'Stripe', 'Payoneer'].map(method => (
                <label key={method} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={paymentSettings.paymentMethods.includes(method)}
                    onChange={(e) => {
                      const methods = e.target.checked
                        ? [...paymentSettings.paymentMethods, method]
                        : paymentSettings.paymentMethods.filter(m => m !== method);
                      handlePaymentChange('paymentMethods', methods);
                    }}
                  />
                  {method}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h2>Security Configuration</h2>

      <div className="settings-grid">
        <div className="setting-group">
          <h3>Authentication</h3>
          <div className="setting-item">
            <label>Session Timeout (minutes)</label>
            <input
              type="number"
              value={securitySettings.sessionTimeout}
              onChange={(e) => handleSecurityChange('sessionTimeout', parseInt(e.target.value))}
              className="setting-input"
              min="5"
              max="43200"
            />
          </div>
          <div className="setting-item">
            <label>Max Login Attempts</label>
            <input
              type="number"
              value={securitySettings.maxLoginAttempts}
              onChange={(e) => handleSecurityChange('maxLoginAttempts', parseInt(e.target.value))}
              className="setting-input"
              min="3"
              max="10"
            />
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={securitySettings.enableTwoFA}
                onChange={(e) => handleSecurityChange('enableTwoFA', e.target.checked)}
              />
              Enable Two-Factor Authentication
            </label>
          </div>
        </div>

        <div className="setting-group">
          <h3>Password Policy</h3>
          <div className="setting-item">
            <label>Minimum Password Length</label>
            <input
              type="number"
              value={securitySettings.passwordMinLength}
              onChange={(e) => handleSecurityChange('passwordMinLength', parseInt(e.target.value))}
              className="setting-input"
              min="6"
              max="32"
            />
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={securitySettings.passwordRequireSpecialChars}
                onChange={(e) => handleSecurityChange('passwordRequireSpecialChars', e.target.checked)}
              />
              Require Special Characters
            </label>
          </div>
        </div>

        <div className="setting-group">
          <h3>API Security</h3>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={securitySettings.enableApiRateLimit}
                onChange={(e) => handleSecurityChange('enableApiRateLimit', e.target.checked)}
              />
              Enable API Rate Limiting
            </label>
          </div>
          <div className="setting-item">
            <label>API Rate Limit (per minute)</label>
            <input
              type="number"
              value={securitySettings.apiRateLimitPerMinute}
              onChange={(e) => handleSecurityChange('apiRateLimitPerMinute', parseInt(e.target.value))}
              className="setting-input"
              min="10"
              max="10000"
              disabled={!securitySettings.enableApiRateLimit}
            />
          </div>
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={securitySettings.enableAuditLog}
                onChange={(e) => handleSecurityChange('enableAuditLog', e.target.checked)}
              />
              Enable Audit Logging
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>System Settings</h1>
        <div className="header-actions">
          {hasChanges && (
            <div className="changes-indicator">
              <span className="changes-text">Unsaved changes</span>
            </div>
          )}
          <button className="btn btn-secondary" onClick={handleReset}>
            Reset to Defaults
          </button>
          <button
            className={`btn btn-primary ${hasChanges ? 'highlight' : ''}`}
            onClick={handleSave}
          >
            Save Settings
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="settings-tabs">
        <button
          className={`tab-btn ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          System
        </button>
        <button
          className={`tab-btn ${activeTab === 'tracking' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracking')}
        >
          Tracking
        </button>
        <button
          className={`tab-btn ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          Payments
        </button>
        <button
          className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
      </div>

      {/* Content */}
      <div className="settings-content">
        {activeTab === 'system' && renderSystemSettings()}
        {activeTab === 'tracking' && renderTrackingSettings()}
        {activeTab === 'payment' && renderPaymentSettings()}
        {activeTab === 'security' && renderSecuritySettings()}
      </div>
    </div>
  );
};

export default Settings;